import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { ReactElement } from "react"
import { FormProvider, useForm } from "react-hook-form"

import EquivalentProductGroupForm from "../forms/EquivalentProductGroupForm"
import {
  type EquivalentProductGroupFormValues,
  equivalentProductGroupFormSchema,
} from "../forms/equivalentProductGroupSchema"
import useEquivalentProductGroupDialogActions from "../hooks/useEquivalentProductGroupDialogActions"
import { buildDefaultFormValues } from "../utils"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { EquivalencyGroup } from "@/graphql/codegen/graphql"

interface EquivalentProductGroupFormDialogProps {
  onClose: () => void
  onSuccess?: () => void
  data?: EquivalencyGroup
}

const defaultFormValues: EquivalentProductGroupFormValues = {
  name: "",
  type: undefined as any,
  notes: "",
}

const EquivalentProductGroupFormDialog = ({
  onClose,
  onSuccess,
  data,
}: EquivalentProductGroupFormDialogProps): ReactElement => {
  const isCreateMode = !data

  const methods = useForm<EquivalentProductGroupFormValues>({
    defaultValues: data ? buildDefaultFormValues(data) : defaultFormValues,
    resolver: zodResolver(equivalentProductGroupFormSchema),
    mode: "onChange",
  })

  const { setIsCancelConfirmationOpen, isCancelConfirmationOpen, canSubmit, handleSubmit, handleCancel } =
    useEquivalentProductGroupDialogActions({
      methods,
      onSuccess: async () => {
        onSuccess?.()
      },
      onClose,
      data,
    })

  const dialogTitle = isCreateMode ? "New Equivalents Group" : "Edit Equivalents Group"

  return (
    <Dialog open onClose={handleCancel} maxWidth='sm' fullWidth data-testid='equivalent-group-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogHeader title={dialogTitle} onClose={handleCancel} />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <EquivalentProductGroupForm isEditMode={!isCreateMode} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              onClick={handleSubmit}
              type='submit'
              disabled={!canSubmit}
              data-testid='submit-button'
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default EquivalentProductGroupFormDialog
